import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import { getBenefitPlanFieldsCacheKey } from "@platform/document-library";
import { useRfpCarriers } from "@platform/hooks";
import {
  useProposalDocumentsQuery,
  type CarrierProposal,
  type ProposalDocument,
} from "@platform/propelx-documents";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import useToastNotificationService from "../../../../../../../hooks/useToastNotificationService";
import { buildCarrierNameById } from "../utils/quotesCarrierNames";

type DocumentStatus = ProposalDocument["status"];

interface TrackedDocument {
  status: DocumentStatus;
  carrier: string;
  name: string;
}

const extractedToastMessage = (carrier: string, documentName: string): string =>
  `${carrier}: ${documentName} extracted`;

const errorToastMessage = (carrier: string, documentName: string): string =>
  `${carrier}: ${documentName} failed to extract`;

const buildDocumentMap = (
  proposals: CarrierProposal[],
): Map<string, TrackedDocument> =>
  new Map(
    proposals.flatMap((proposal) =>
      proposal.documents.map(
        (document) =>
          [
            document.id,
            {
              status: document.status,
              carrier: proposal.carrier,
              name: document.name,
            },
          ] as const,
      ),
    ),
  );

const toStatusMap = (
  documents: Map<string, TrackedDocument>,
): Map<string, DocumentStatus> =>
  new Map([...documents].map(([id, document]) => [id, document.status]));

/**
 * Watches the proposal-document poll (`useProposalDocumentsQuery`) for documents
 * that finish AI extraction and surfaces the result to the broker without a hard
 * refresh:
 *
 * - `Processing → Extracted`: a success toast per document, then invalidates the
 *   proposals and benefit-plan-fields queries so the SBS grid reloads the
 *   extracted plan values and AI annotations.
 * - `Processing → Error`: an error toast per document.
 *
 * Documents already terminal on first render are seeded silently so only
 * transitions observed while mounted produce a toast.
 */
export const useProposalExtractionToast = (
  ecoRfpId: string,
  rfpId: string | undefined,
): void => {
  const { proposals } = useProposalDocumentsQuery(ecoRfpId);
  const { createToastNotification } = useToastNotificationService();
  const queryClient = useQueryClient();

  // Resolve the carrier's shareIdentifier (what the proposal documents carry) to its display
  // name for the toast. Read through a ref so the effect doesn't depend on this async query.
  const { data: carrierNameById } = useRfpCarriers<Map<string, string>>({
    rfpId: rfpId ?? "",
    enabled: !!rfpId,
    select: buildCarrierNameById,
  });
  const carrierNameByIdRef = useRef(carrierNameById);
  carrierNameByIdRef.current = carrierNameById;

  const previousStatusesRef = useRef<Map<string, DocumentStatus>>(new Map());
  const initializedRef = useRef(false);

  useEffect(() => {
    const currentDocuments = buildDocumentMap(proposals);

    // Seed on the first data tick so documents already terminal on mount don't
    // toast — only transitions observed while mounted should notify.
    if (!initializedRef.current) {
      if (currentDocuments.size === 0) return;
      previousStatusesRef.current = toStatusMap(currentDocuments);
      initializedRef.current = true;
      return;
    }

    const previousStatuses = previousStatusesRef.current;
    const newlyExtractedDocumentIds: string[] = [];

    currentDocuments.forEach((document, id) => {
      const wasProcessing = previousStatuses.get(id) === "Processing";
      if (!wasProcessing) return;

      const carrierName =
        carrierNameByIdRef.current?.get(document.carrier) ?? document.carrier;

      if (document.status === "Extracted") {
        createToastNotification({
          severity: "success",
          message: extractedToastMessage(carrierName, document.name),
        });
        newlyExtractedDocumentIds.push(id);
      } else if (document.status === "Error") {
        createToastNotification({
          severity: "error",
          message: errorToastMessage(carrierName, document.name),
        });
      }
    });

    if (newlyExtractedDocumentIds.length > 0) {
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(ecoRfpId),
      });
      newlyExtractedDocumentIds.forEach((id) => {
        void queryClient.invalidateQueries({
          queryKey: getBenefitPlanFieldsCacheKey(id),
        });
      });
    }

    previousStatusesRef.current = toStatusMap(currentDocuments);
  }, [proposals, createToastNotification, queryClient, ecoRfpId]);
};
